"use client";

import Link from "next/link";
import React, { useId } from "react";

// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";

// import required modules
import { Pagination } from "swiper/modules";
import Typography from "./Typography";

interface Hotel {
  id: number | string;
  name: string;
  image: string;
  address: string;
  to: string;
}

interface HotelsSliderProps {
  title: string;
  subtitle?: string;
  hotels: Array<Hotel>;
}

const HotelSlide = ({ hotel }: { hotel: Hotel }) => {
  return (
    <Link href={hotel.to} className="group block">
      <div className="relative h-64 w-full overflow-hidden rounded-sm md:h-72">
        <img
          src={hotel.image}
          alt={hotel.name}
          width={360}
          height={288}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 block h-full w-full object-cover transition duration-300 group-hover:scale-105"
        />
      </div>
      <div className="mt-2 min-w-0">
        <Typography variant="h3" className="truncate text-sm font-semibold">
          {hotel.name}
        </Typography>
        <Typography
          variant="body2"
          className="mt-1 block truncate text-xs text-foreground/70"
        >
          {hotel.address}
        </Typography>
      </div>
    </Link>
  );
};

const HotelsSlider = ({ title, subtitle, hotels }: HotelsSliderProps) => {
  const id = useId();

  const paginationClass = `pagination-${id.replaceAll(":", "-")}`;

  return (
    <section className="mb-7 lg:mb-10">
      <div className="mb-4">
        <Typography variant="h2" className="text-lg font-bold md:text-xl">
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            className="mt-1 block text-sm text-foreground/70"
          >
            {subtitle}
          </Typography>
        )}
      </div>
      <Swiper
        modules={[Pagination]}
        loop={hotels.length > 6}
        slidesPerView={2}
        spaceBetween={12}
        breakpoints={{
          768: { slidesPerView: 3, spaceBetween: 14 },
          1024: { slidesPerView: 4, spaceBetween: 16 },
        }}
        pagination={{
          el: `.${paginationClass}`,
          clickable: true,
        }}
      >
        {hotels.map((hotel) => (
          <SwiperSlide key={hotel.id}>
            <HotelSlide hotel={hotel} />
          </SwiperSlide>
        ))}
      </Swiper>

      <div className={`${paginationClass} mt-2 text-center`} />
    </section>
  );
};

export default HotelsSlider;
